import React, { FC, useCallback, useContext, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import { Icon, Loader } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { installableAppUploadImage } from 'client/api/appStoreApi';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormErrors } from 'client/hocs/types';
import { InstallableAppFormSettings } from 'client/types';
import { translateError } from 'client/utils/errorMessage';
import { ACCEPTED_LOGO_FORMATS } from 'client/utils/settings/common';
import * as appFormKeyset from 'i18n/AppForm';
import * as keyset from 'i18n/Settings';

const t = i18n(keyset);
const tAppForm = i18n(appFormKeyset);

import styles from './FilesUploaders.module.scss';

interface Props {
    disabled: boolean;
    logoUrl?: string | null;
    setErrors?: (errors: FormErrors<InstallableAppFormSettings & object>, cb?: () => void) => void;
    onIdChange?: (logoUrl: string) => void;
    onUrlChange?: (logoUrl: string) => void;
    logoType: 'square' | 'secondary';
}

const mapFieldName = {
    square: 'squareLogoId',
    secondary: 'secondaryLogoId',
};

const createLogo = (logoUrl: string, isLoading: boolean, isDragActive: boolean, logoType: 'square' | 'secondary') => {
    const containerStyle = [
        styles.appIcon__container,
        logoType === 'square' && styles.appIcon__container_square,
        logoType === 'secondary' && styles.appIcon__container_smartScreenSecondary,
    ];

    let resolution = '500x500';
    if (logoType === 'secondary') resolution = '280x200';

    if (isLoading) {
        return (
            <div className={clx(...containerStyle, styles.appIcon_loading)}>
                <Loader size="s" />
            </div>
        );
    }
    if (isDragActive) {
        return (
            <div className={clx(...containerStyle, styles.appIcon_empty, styles.appIcon_smallBorderStroke)}>
                <div>{tAppForm('load-here')}</div>
            </div>
        );
    }
    if (logoUrl)
        return (
            <div
                className={clx(...containerStyle, styles.appIcon_preview)}
                style={{ backgroundImage: `url(${logoUrl})` }}
            />
        );
    return (
        <div className={clx(...containerStyle, styles.appIcon_empty, styles.appIcon_smallBorderStroke)}>
            <Icon data={FileArrowUp} size={15} />
            <div className={styles.appIcon__resolution}>{resolution}</div>
            <div className={clx(styles.appIcon__label, styles.appIcon__label_small)}>{t('icon')}</div>
        </div>
    );
};

export const LogoUploader: FC<Props> = ({ disabled, logoUrl, onIdChange, onUrlChange, setErrors, logoType }) => {
    const [isLoading, setIsLoading] = useState(false);
    const { aliceApp } = useContext(CurrentAliceAppContext)!;

    const onDrop = useCallback((acceptedFiles) => {
        setIsLoading(true);

        installableAppUploadImage(aliceApp.id, aliceApp.channel, acceptedFiles[0], 'appTileImage')
            .then((image) => {
                onIdChange && onIdChange(image.id);
                onUrlChange && onUrlChange(image.url);
                setIsLoading(false);
            })
            .catch((err) => {
                const { message } = err;
                setErrors && setErrors({ [mapFieldName[logoType]]: translateError(message) });
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        accept: [...ACCEPTED_LOGO_FORMATS],
    });

    return (
        <div
            className={clx(styles.uploaderContainer, disabled && styles.uploaderContainer_disabled)}
            {...getRootProps()}
        >
            <input {...getInputProps()} />
            {createLogo(logoUrl || '', isLoading, isDragActive, logoType)}
        </div>
    );
};
